import Ansi from "@cocalc/ansi-to-react";
import type {
  ScriptBlock,
  ScriptOpts,
  ExecutedScript as TExecutedScript,
} from "@gearbox-protocol/deploy-tools-shared";
import { ErrorBox, SourceLink } from "@gearbox-protocol/deploy-tools-ui";
import GrafanaLink from "components/GrafanaLink";
import StatusDot from "components/StatusDot";
import { formatDuration, intervalToDuration } from "date-fns";
import type { FC } from "react";

export interface ScriptProps {
  executionId: string;
  script: TExecutedScript;
}

function getDelta(
  before?: ScriptBlock,
  after?: ScriptBlock,
): { blocks: number; duration: string } | undefined {
  if (!before || !after) {
    return undefined;
  }
  const blocks = after.number - before.number;
  const duration =
    formatDuration(
      intervalToDuration({
        start: new Date(before.time),
        end: new Date(after.time),
      }),
    ) || "0 seconds";
  return { blocks, duration };
}

export const Script: FC<ScriptProps> = ({ script, executionId }) => {
  const { id, description } = script.script;
  const { status, duration, error, blockBefore, blockAfter, source, revision } =
    script;
  const delta = getDelta(blockBefore, blockAfter);
  return (
    <li>
      <div className="flex flex-row items-center gap-2">
        <StatusDot status={status} className="inline-block" />
        {description || id}
        <GrafanaLink
          executionId={executionId}
          labels={{ role: id }}
          className="text-slate-400 font-mono text-sm border-l-slate-400 border-l pl-1"
        />
        <SourceLink
          source={source}
          revision={revision}
          className="text-slate-400 font-mono text-sm border-l-slate-400 border-l pl-1"
        />
        {!!duration && (
          <span className="pl-1 font-mono text-sm text-slate-400 border-l-slate-400 border-l">
            executed in {duration}
          </span>
        )}
        {!!delta?.blocks && (
          <span className="pl-1 font-mono text-sm text-slate-400 border-l-slate-400 border-l">
            {delta.blocks} blocks
            <span className="ml-1 pl-1 border-l-slate-400 border-l">
              {delta.duration}
            </span>
          </span>
        )}
      </div>
      <p className="text-slate-400 text-sm font-mono ml-4">
        {cliCommand(script.script)}
      </p>
      {status === "error" && error && (
        <ErrorBox error={error} className="ml-4" />
      )}
      {status === "failed" && error && (
        <pre className="bg-gray-950 rounded-sm text-sm text-wrap font-mono ml-4 p-4 overflow-x-scroll">
          <Ansi>{error}</Ansi>
        </pre>
      )}
    </li>
  );
};

export interface ScriptTemplateProps {
  script: ScriptOpts;
}

export const ScriptTemplate: FC<ScriptTemplateProps> = ({ script }) => {
  const { id, description } = script;
  return (
    <li>
      <div className="flex flex-row items-center gap-2">
        <StatusDot status="queued" className="inline-block" />
        {description || id}
      </div>
      <p className="text-slate-400 text-sm font-mono ml-4">
        {cliCommand(script)}
      </p>
    </li>
  );
};

function cliCommand(opts: ScriptOpts): string {
  const parts: string[] = [];
  if (opts.npx) {
    parts.push("npx", opts.npx);
  } else if (opts.docker) {
    parts.push("docker", "run", opts.docker);
  }
  return [...parts, ...(opts.args ?? [])].join(" ");
}
